import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ImageIcon, Check, Loader2, Coins, X } from "lucide-react";

export const BackgroundButton = ({ horseId, currentImage, onApply }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => gameApi.get("/game/backgrounds/catalog").then((r) => setData(r.data)).catch(() => setData({ shop: [], owned: [], currency: 0 }));
  useEffect(() => { if (open) load(); }, [open]);

  const equip = async (bg, force) => {
    setBusy(true);
    try {
      await gameApi.post("/game/backgrounds/equip", { bg_id: bg.bg_id, horse_id: horseId, force_move: !!force });
      toast.success(`${bg.name} equipped`);
      onApply?.(bg.image_path);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not equip."); }
    finally { setBusy(false); }
  };
  const useDefault = async () => {
    setBusy(true);
    try { await gameApi.post("/game/backgrounds/unequip", { horse_id: horseId }); toast.success("Default background restored"); onApply?.(null); setOpen(false); }
    catch { toast.error("Could not update."); } finally { setBusy(false); }
  };
  const buy = async (b) => {
    setBusy(true);
    try { await gameApi.post("/game/backgrounds/buy", { bg_id: b.id }); toast.success(`Purchased ${b.name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not buy."); } finally { setBusy(false); }
  };

  const ownedIds = new Set((data?.owned || []).map((o) => o.bg_id));
  const buyable = (data?.shop || []).filter((b) => !ownedIds.has(b.id) || true); // always show to buy more copies

  return (
    <>
      <button data-testid="change-bg-btn" onClick={() => setOpen(true)}
        className="mt-2 flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-bold text-secondary active:scale-95">
        <ImageIcon size={14} /> Background
      </button>
      {open && (
        <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="bg-modal" onClick={() => setOpen(false)}>
          <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-card p-4 shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <p className="font-display text-lg font-bold text-primary">Change Background</p>
              <button data-testid="bg-close" onClick={() => setOpen(false)} className="rounded-full p-1 text-muted-foreground hover:text-primary"><X size={18} /></button>
            </div>
            {!data ? <p className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="inline animate-spin" size={15} /> Loading…</p> : (
              <div className="space-y-2">
                <button data-testid="bg-default" onClick={useDefault} disabled={busy}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-muted/30 p-2 text-left active:scale-[0.99]">
                  <div className="grid h-12 w-16 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-muted to-background text-[10px] font-bold text-muted-foreground">Neutral</div>
                  <div className="flex-1"><p className="text-sm font-bold text-primary">Default Background</p><p className="text-[11px] text-muted-foreground">The standard neutral profile background</p></div>
                  {!currentImage && <Check size={16} className="text-primary" />}
                </button>
                {(data.owned || []).length > 0 && <p className="pt-1 text-xs font-bold uppercase tracking-wider text-secondary">Your Backgrounds</p>}
                {(data.owned || []).map((o) => {
                  const here = currentImage && o.image_path === currentImage;
                  const elsewhere = o.equipped_elsewhere?.filter((e) => e.horse_id !== horseId) || [];
                  const canEquipFree = o.available > 0;
                  return (
                    <div key={o.bg_id} data-testid={`bg-owned-${o.bg_id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-2">
                      <img src={o.image_path} alt="" className="h-12 w-16 shrink-0 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-primary">{o.name}</p>
                        <p className="text-[11px] text-muted-foreground">Owned {o.owned} · Available {o.available}{elsewhere.length ? ` · On ${elsewhere.map((e) => e.horse_name || "a horse").join(", ")}` : ""}</p>
                      </div>
                      {here ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Equipped here</span>
                        : canEquipFree ? <button data-testid={`bg-equip-${o.bg_id}`} disabled={busy} onClick={() => equip(o, false)} className="rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground active:scale-95">Equip</button>
                        : <button data-testid={`bg-move-${o.bg_id}`} disabled={busy} onClick={() => equip(o, true)} className="rounded-full border border-amber-400 px-2.5 py-1 text-[11px] font-bold text-amber-600 active:scale-95">Move here</button>}
                    </div>
                  );
                })}
                <p className="pt-2 text-xs font-bold uppercase tracking-wider text-secondary">Shop — buy with coins ({data.currency}🪙)</p>
                {(buyable || []).map((b) => (
                  <div key={b.id} data-testid={`bg-shop-${b.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-2">
                    <img src={b.image_path} alt="" className="h-12 w-16 shrink-0 rounded-lg object-cover" />
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-primary">{b.name}</p><p className="flex items-center gap-1 text-[11px] font-semibold text-secondary"><Coins size={11} /> {b.price}</p></div>
                    <button data-testid={`bg-buy-${b.id}`} disabled={busy} onClick={() => buy(b)} className="rounded-full border border-secondary px-3 py-1 text-xs font-bold text-secondary active:scale-95">Buy</button>
                  </div>
                ))}
                {(!buyable || buyable.length === 0) && <p className="py-2 text-center text-xs text-muted-foreground">No backgrounds in the shop yet.</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
